import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter, forwardRef, Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {BehaviorSubject, Observable} from 'rxjs';
import {KEYBOARD_KEY_PRESSED} from "../../../providers/keyboardEvents.provider";
import {DestroyStreamService} from "../../../services";
import {takeUntil} from "rxjs/operators";
import {INT_TAG_TOKEN} from "./int-tag.token";

@Component({
  selector: 'int-tag',
  templateUrl: './int-tag.component.html',
  styleUrls: ['./int-tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyStreamService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: IntTagComponent,
      multi: true,
    },
    {
      provide: INT_TAG_TOKEN,
      useExisting: forwardRef(() => IntTagComponent),
      multi: true,
    }
  ],
})
export class IntTagComponent
  implements ControlValueAccessor, OnInit, AfterViewInit
{
  list$ = new BehaviorSubject<Array<string>>([]);

  enterValue$ = new BehaviorSubject<string>('');

  focusStatus$ = new BehaviorSubject<boolean>(false);

  removeIndex: number | null = null;

  @ViewChild('field') private readonly field!: ElementRef;
  @Output() onChangeEvent = new EventEmitter<string>();
  @Output() addEvent = new EventEmitter<string>();

  @Output() removeBreakEvent = new EventEmitter<() => void>();
  @Output() addBreakEvent = new EventEmitter<() => void>();

  @Output() onFocus = new EventEmitter();
  @Output() onBlur = new EventEmitter();

  @Input() validator = () => true;
  @Input() validatorStream: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  @Input() useCallbackBeforeAction = false;

  constructor(
    @Inject(KEYBOARD_KEY_PRESSED) private readonly keyStream$: Observable<string>,
    private readonly destroy$: DestroyStreamService
  ) {
    this.keyStream$.pipe(takeUntil(this.destroy$)).subscribe((key) => {
      if(key === 'Enter') {
        this.add();
      }
    });
  }

  get isValid(): boolean {
    return !!this.enterValue$.value && (this.validator() || this.validatorStream.value);
  }

  focus() {
    this.field.nativeElement.focus();
  }

  onFocusHandle() {
    this.focusStatus$.next(true);
    this.onFocus.emit();
  }

  onBlurHandle() {
    this.focusStatus$.next(false);
    this.onBlur.emit();
  }

  private _remove(index: number) {
    this.list$.next(this.list$.value.filter((_, i) => i !== index));
    this.onChange(this.list$.value);
  }

  removeBreakHandler(index: number) {
    this._remove(index)
  }

  remove(index: number) {

    this.removeIndex = index;

    if(this.useCallbackBeforeAction) {
      this.removeBreakEvent.emit(this.removeBreakHandler.bind(this, index))
    }else{
      this._remove(index);
    }
  }

  setEnterValue(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.enterValue$.next(value);
    this.onChangeEvent.emit(value);
  }

  private _add() {
    if (this.isValid) {
      this.addEvent.next(this.enterValue$.value);
      this.list$.next([...this.list$.value, this.enterValue$.value]);
      this.onChange(this.list$.value);
      this.clearField();
    }
  }

  addBreakHandler() {
    this._add();
  }

  add() {
    if(this.useCallbackBeforeAction) {
      this.addBreakEvent.emit(this.addBreakHandler.bind(this))
    }else{
      this._add();
    }
  }

  clearField() {
    this.field.nativeElement.value = '';
    this.enterValue$.next('');
  }

  ngAfterViewInit() {}

  ngOnInit() {}

  writeValue(value: any) {
    this.list$.next(value);
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  markAsTouched() {}

  onChange(val: any) {}

  onTouched() {}
}
